<template>
    <div class="min-h-screen bg-slate-950 text-white">
        <div class="max-w-6xl mx-auto px-4 py-16">
            <!-- Header -->
            <header class="text-center mb-12">
                <p class="text-sm uppercase tracking-[0.25em] text-dashboard-primary-blue mb-3">Blackweek-Special</p>
                <h1 class="text-3xl md:text-4xl font-semibold mb-4">HKCM Mini Masterclass</h1>
                <p class="text-slate-300 max-w-3xl mx-auto text-sm md:text-base">
                    Unsere erste offizielle HKCM Mini Masterclass zeigt dir, wie unsere drei führenden Experten denken, analysieren und handeln. In drei präzisen Videomodulen
                    lernst du das Fundament erfolgreichen Investierens: Klares Mindset, strukturierter Portfolioaufbau und ein grundlegendes Verständnis für Marktbewegungen.
                </p>
            </header>

            <!-- DESKTOP: Active Video + Selection Cards -->
            <div class="hidden md:block gap-4">
                <!-- Desktop Selection Cards -->
                <p class="text-xs uppercase tracking-[0.25em] text-slate-400 mb-8 text-center w-full">Wähle ein Modul</p>
                <section class="hidden md:grid md:grid-cols-3 gap-4 md:gap-6 mb-8">
                    <article
                        v-for="(video, index) in videos"
                        :key="video.id"
                        class="bg-slate-900/70 rounded-2xl shadow-lg relative group cursor-pointer overflow-visible"
                        :class="index === activeVideoIndex ? 'border border-dashboard-primary-blue' : 'border border-slate-800'"
                        @click="activateVideo(index)"
                    >
                        <span
                            class="absolute -top-5 left-1/2 -translate-x-1/2 rounded-full w-10 h-10 flex items-center justify-center text-white text-sm font-semibold z-10"
                            :class="index === activeVideoIndex ? 'bg-dashboard-primary-blue' : 'bg-slate-700'"
                        >
                            {{ index + 1 }}
                        </span>

                        <div class="mt-2 p-4 md:p-5">
                            <div class="flex items-center justify-between mb-1.5">
                                <h2 class="text-sm md:text-base font-semibold line-clamp-2">
                                    {{ video.title }}
                                </h2>
                            </div>
                            <p class="text-[11px] md:text-xs text-slate-400 mb-2">
                                {{ video.description }}
                            </p>
                            <p class="text-[11px] md:text-xs text-slate-500">Länge: {{ video.length }}</p>
                        </div>
                    </article>
                </section>
                <!-- Active Video Section -->
                <Transition name="fade" mode="out-in">
                    <section v-if="activeVideo" :key="activeVideo.id" ref="heroSection" class="mb-10">
                        <article class="bg-slate-900/70 border border-slate-800 rounded-2xl shadow-lg overflow-hidden ring-2 ring-dashboard-primary-blue">
                            <div class="aspect-video flex items-center justify-center border border-white/30" :class="activeVideo.bg">
                                <div class="w-full h-full rounded-full flex items-center justify-center backdrop-blur relative">
                                    <iframe
                                        :src="`https://iframe.mediadelivery.net/embed/369702/${activeVideo.id}?autoplay=false&loop=false&muted=false&preload=true&responsive=true`"
                                        loading="lazy"
                                        style="border: 0; position: absolute; top: 0; height: 100%; width: 100%"
                                        allow="accelerometer;gyroscope;autoplay;encrypted-media;picture-in-picture;"
                                        allowfullscreen="true"
                                    ></iframe>
                                </div>
                            </div>
                            <div class="p-5">
                                <h2 class="text-lg font-semibold mb-1">{{ activeVideo.title }}</h2>
                                <p class="text-xs text-slate-400 mb-3 text-block">
                                    {{ activeVideo.description }}
                                </p>
                                <p class="text-xs text-slate-500">Länge: {{ activeVideo.length }}</p>
                            </div>
                        </article>
                    </section>
                </Transition>
            </div>

            <!-- MOBILE -->
            <section class="space-y-6 md:hidden">
                <article v-for="video in videos" :key="video.id + '-mobile'" class="bg-slate-900/70 border border-slate-800 rounded-2xl shadow-lg overflow-hidden">
                    <div class="aspect-video flex items-center justify-center border-b border-white/5 relative" :class="video.bg">
                        <div class="w-full h-full rounded-full flex items-center justify-center backdrop-blur relative">
                            <iframe
                                :src="`https://iframe.mediadelivery.net/embed/369702/${video.id}?autoplay=false&loop=false&muted=false&preload=true&responsive=true`"
                                loading="lazy"
                                style="border: 0; position: absolute; top: 0; height: 100%; width: 100%"
                                allow="accelerometer;gyroscope;autoplay;encrypted-media;picture-in-picture;"
                                allowfullscreen="true"
                            ></iframe>
                        </div>
                    </div>

                    <div class="p-4">
                        <h2 class="text-base font-semibold mb-1">
                            {{ video.title }}
                        </h2>
                        <p class="text-xs text-slate-400 mb-2">
                            {{ video.description }}
                        </p>
                        <p class="text-xs text-slate-500">Länge: {{ video.length }}</p>
                    </div>
                </article>
            </section>

            <!-- Comment Section -->
            <section class="mt-16 max-w-2xl mx-auto">
                <div class="bg-slate-900/70 border border-slate-800 rounded-2xl p-6 shadow-lg">
                    <h2 class="text-xl font-semibold mb-4">Hinterlasse uns einen Kommentar</h2>
                    <p class="text-sm text-slate-400 mb-6">Teile uns deine Gedanken zur Masterclass mit. Dein Feedback hilft uns, unseren Content weiter zu verbessern.</p>

                    <form class="space-y-4" @submit.prevent="submitComment">
                        <div>
                            <textarea
                                id="comment"
                                v-model="commentText"
                                rows="8"
                                maxlength="1000"
                                :disabled="loading"
                                class="w-full px-4 py-3 bg-slate-800/50 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-dashboard-primary-blue focus:ring-1 focus:ring-dashboard-primary-blue transition-colors disabled:opacity-50 disabled:cursor-not-allowed resize-y min-h-[160px]"
                                placeholder="Schreibe hier deinen Kommentar..."
                            ></textarea>
                            <div class="flex justify-between items-center mt-2">
                                <span v-if="commentError" class="text-xs text-red-400">{{ commentError }}</span>
                                <span v-else class="text-xs text-slate-500"></span>
                                <span class="text-xs text-slate-500">{{ commentText.length }} / 1000</span>
                            </div>
                        </div>

                        <button
                            type="submit"
                            :disabled="loading || !commentText.trim()"
                            class="w-full bg-dashboard-primary-blue hover:bg-blue-600 disabled:bg-slate-700 disabled:cursor-not-allowed text-white font-medium py-3 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center"
                        >
                            <span v-if="!loading">Feedback absenden</span>
                            <span v-else class="flex items-center">
                                <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                    <path
                                        class="opacity-75"
                                        fill="currentColor"
                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                    ></path>
                                </svg>
                                Wird gesendet...
                            </span>
                        </button>

                        <!-- Success Message -->
                        <div v-if="successMessage" class="p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                            <p class="text-sm text-emerald-400">{{ successMessage }}</p>
                        </div>

                        <!-- Error Message -->
                        <div v-if="errorMessage" class="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                            <p class="text-sm text-red-400">{{ errorMessage }}</p>
                        </div>
                    </form>
                </div>
            </section>

            <!-- Footer Text -->
            <footer class="mt-12 text-xs text-slate-500 max-w-2xl text-center justify-center mx-auto">
                Diese Masterclass wurde im Rahmen der Blackweek 2025 veröffentlicht und steht Ihnen auch nach dem Aktionszeitraum zur Verfügung. Wir wünschen Ihnen viel Vergnügen.
            </footer>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, nextTick } from 'vue';

const videos = [
    {
        id: 'b5b8704d-f73e-44dc-ae73-ea2cc9c2692a',
        title: 'Mindset beim Handeln',
        description:
            'Philip Hopf zeigt dir, warum 90 % der Investoren nicht am Markt scheitern – sondern an sich selbst. ' +
            'Du lernst emotionale Kontrolle, klares Denken, Disziplin und langfristige Entscheidungslogik. ' +
            'Dein Fundament vor jeder Investition.',
        length: 'ca. 30 Min · Level: Alle',
        bg: 'bg-gradient-to-br from-purple-500/30 via-slate-800 to-emerald-500/40'
    },
    {
        id: 'f1a3915d-dad8-4bd7-9378-cb366421bc19',
        title: 'Portfoliomanagement',
        description:
            'Philip Klinkmüller erklärt, wie ein robustes Portfolio wirklich aufgebaut wird: Gewichtung, Risiko, Exit-Strategien, Cash-Management, Rebalancing. ' +
            'Danach verstehst du, wie man Chancen nutzt, ohne ins Chaos abzurutschen – klar, regelbasiert, systematisch.',
        length: 'ca. 30 Min · Level: Alle',
        bg: 'bg-gradient-to-br from-emerald-500/30 via-slate-800 to-blue-500/40'
    },
    {
        id: '96c4f866-c09c-40f8-a94f-65bf13f8fcbc',
        title: 'Marktanalyse',
        description:
            'Das Phantom zeigt dir, wie du Impulse, Korrekturen, Zonen und Szenarien erkennst. ' +
            'Du lernst die Grundlagen unserer Analysemethode, arbeitest mit klaren Wenn-Dann-Regeln und wirst danach verstehen, wie man mit verschiedenen Szenarien umgeht.',
        length: 'ca. 24 Min · Level: Alle',
        bg: 'bg-gradient-to-br from-purple-500/30 via-slate-800 to-emerald-500/40'
    }
];

const activeVideoIndex = ref(0);
const heroSection = ref(null);
const commentText = ref('');
const loading = ref(false);
const successMessage = ref('');
const errorMessage = ref('');
const commentError = ref('');

const activeVideo = computed(() => videos[activeVideoIndex.value] ?? null);

const activateVideo = (index) => {
    if (index === activeVideoIndex.value) return;

    activeVideoIndex.value = index;

    if (window.innerWidth >= 768) {
        nextTick(() => {
            if (heroSection.value) {
                heroSection.value.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start'
                });
            }
        });
    }
};

// Submit comment
const submitComment = async () => {
    // Reset messages
    successMessage.value = '';
    errorMessage.value = '';
    commentError.value = '';

    // Validate
    if (!commentText.value.trim()) {
        commentError.value = 'Bitte gib einen Kommentar ein.';

        return;
    }

    if (commentText.value.length > 1000) {
        commentError.value = 'Der Kommentar darf maximal 1000 Zeichen lang sein.';

        return;
    }

    loading.value = true;

    try {
        const response = await fetch('/api/v1/blackWeekComments', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json'
            },
            body: JSON.stringify({
                text: commentText.value.trim()
            })
        });

        const data = await response.json();

        if (response.ok) {
            successMessage.value = 'Vielen Dank für dein Feedback! Deine Nachricht wurde erfolgreich übermittelt.';
            commentText.value = '';
        } else {
            errorMessage.value = data.message || 'Es ist ein Fehler aufgetreten. Bitte versuche es später erneut.';
        }
    } catch (error) {
        console.error('Error submitting comment:', error);
        errorMessage.value = 'Es ist ein Fehler aufgetreten. Bitte versuche es später erneut.';
    } finally {
        loading.value = false;
    }
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
